import React from "react";
import { OpeningsLogo } from "./OpeningsLogo";

export interface OpeningsNotFoundAction {
  href: string;
  label: string;
}

export interface OpeningsNotFoundProps {
  title: string;
  description: string;
  action: OpeningsNotFoundAction;
  /** Small label above the title. */
  eyebrow?: string;
}

// Back-office token variables (`--app-*`, from @openings/design-tokens) with
// the same values as fallbacks, so the page also renders in an app that does
// not set them.
const color = {
  background: "var(--app-color-background, #f5f5f5)",
  surface: "var(--app-color-surface, #ffffff)",
  borderSecondary: "var(--app-color-border-secondary, #f0f0f0)",
  text: "var(--app-color-text, rgba(0,0,0,0.88))",
  textSecondary: "var(--app-color-text-secondary, rgba(0,0,0,0.65))",
  textTertiary: "var(--app-color-text-tertiary, rgba(0,0,0,0.45))",
  primary: "var(--app-color-primary, #0c0c0f)",
  primaryText: "var(--app-color-primary-text, #fff)",
  primaryBorder: "var(--app-color-primary-border, #8c8c8c)",
};

// Inline styles cannot express hover or focus, so those live here.
const interactionCss = `
.openings-not-found__action { transition: opacity 0.15s ease; }
.openings-not-found__action:hover { opacity: 0.85; }
.openings-not-found__action:focus-visible {
  outline: 2px solid ${color.primaryBorder};
  outline-offset: 2px;
}
`;

/**
 * Openings-branded 404 for the back-office apps (dashboard, staff). Copy is
 * passed in so each app can supply its own wording and translations.
 */
export function OpeningsNotFound({
  title,
  description,
  action,
  eyebrow = "404",
}: OpeningsNotFoundProps) {
  return (
    <main
      style={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        padding: 24,
        background: color.background,
        color: color.text,
      }}
    >
      <style>{interactionCss}</style>
      <OpeningsLogo size="md" />
      <div
        style={{
          boxSizing: "border-box",
          width: "100%",
          maxWidth: 420,
          padding: "40px 32px",
          background: color.surface,
          border: `1px solid ${color.borderSecondary}`,
          borderRadius: 12,
          textAlign: "center",
        }}
      >
        <p
          style={{
            margin: "0 0 12px",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: color.textTertiary,
          }}
        >
          {eyebrow}
        </p>
        <h1
          style={{
            margin: "0 0 8px",
            fontSize: "var(--app-font-size-heading-2, 20px)",
            fontWeight: "var(--app-font-weight-strong, 600)",
            lineHeight: 1.35,
            textWrap: "balance",
          }}
        >
          {title}
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: "var(--app-font-size-lg, 14px)",
            lineHeight: 1.55,
            color: color.textSecondary,
            textWrap: "balance",
          }}
        >
          {description}
        </p>
        <a
          href={action.href}
          className="openings-not-found__action"
          style={{
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: "var(--app-control-height-lg, 40px)",
            padding: "0 20px",
            borderRadius: "var(--app-radius, 6px)",
            background: color.primary,
            color: color.primaryText,
            fontSize: "var(--app-font-size-lg, 14px)",
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          {action.label}
        </a>
      </div>
    </main>
  );
}
